import React, { useMemo } from 'react';
import { POLAROID_LAYOUTS, getPolaroidLayout } from '../../utils/polaroidLayout';

/**
 * FramePreview – CSS-based live preview of an event photo frame: a Polaroid
 * card (portrait + landscape variants) or the organiser's own transparent PNG.
 *
 * Props:
 *   frameId       – 'polaroid_portrait' | 'polaroid_landscape'
 *   frameColor    – frame card color (#FFFFFF default — Polaroid white)
 *   overlay       – custom PNG frame from utils/eventFrame; replaces the Polaroid card
 *   overlayText   – text shown in the bottom caption area
 *   logoUrl       – URL or data-URL of a logo image
 *   logoSize      – 0–200 (percentage of base size), default 100
 *   textSize      – 0–200 (percentage of base font size), default 100
 *   previewImage  – optional base photo URL to show inside the frame
 *   width         – container width in px (default 180)
 */
export default function FramePreview({
    frameId = 'polaroid_portrait',
    frameColor = '#FFFFFF',
    overlay = null,
    overlayText = '',
    logoUrl = '',
    logoSize = 100,
    textSize = 100,
    previewImage = '',
    width = 180,
}) {
    const layout = useMemo(() => getPolaroidLayout(frameId), [frameId]);

    // Custom PNG frame: the photo fills the detected photo area, the PNG sits on top.
    if (overlay) {
        const rect = overlay.photoRect || { x: 0, y: 0, w: 1, h: 1 };
        return (
            <div
                style={{
                    width,
                    height: Math.round(width * (overlay.height / overlay.width)),
                    position: 'relative',
                    borderRadius: 6,
                    overflow: 'hidden',
                    background: '#FFFFFF',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        left: `${rect.x * 100}%`,
                        top: `${rect.y * 100}%`,
                        width: `${rect.w * 100}%`,
                        height: `${rect.h * 100}%`,
                        overflow: 'hidden',
                        background: '#1e293b',
                    }}
                >
                    {previewImage && (
                        <img
                            src={previewImage}
                            alt="preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    )}
                </div>
                <img
                    src={overlay.src}
                    alt="frame"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                />
            </div>
        );
    }

    // Display size — preserve layout aspect ratio
    const height = Math.round(width * (layout.outputHeight / layout.outputWidth));

    // Scale factor from layout pixels → display pixels
    const scale = width / layout.outputWidth;

    const sideMargin   = layout.sideMargin   * scale;
    const topMargin    = layout.topMargin    * scale;
    const bottomMargin = layout.bottomMargin * scale;

    const fontSize = Math.round(40 * scale * (textSize / 100));
    const logoW    = Math.round(layout.outputWidth * 0.18 * scale * (logoSize / 100));

    // Dark text on light bg, light text on dark bg
    const textColor = isLightColor(frameColor) ? '#1a1a1a' : '#FFFFFF';

    return (
        <div
            style={{
                width,
                height,
                position: 'relative',
                borderRadius: 6,
                overflow: 'hidden',
                background: frameColor,
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
        >
            {/* Photo area */}
            <div
                style={{
                    position: 'absolute',
                    top: topMargin,
                    left: sideMargin,
                    right: sideMargin,
                    bottom: bottomMargin,
                    overflow: 'hidden',
                    background: previewImage ? undefined : '#1e293b',
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                }}
            >
                {previewImage ? (
                    <img
                        src={previewImage}
                        alt="preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#94a3b8',
                            fontSize: Math.max(14, width * 0.1),
                        }}
                    >
                        📸
                    </div>
                )}
            </div>

            {/* Bottom caption area (Polaroid signature feature) */}
            <div
                style={{
                    position: 'absolute',
                    left: sideMargin,
                    right: sideMargin,
                    bottom: 0,
                    height: bottomMargin,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    padding: '4px 8px',
                }}
            >
                {logoUrl && (
                    <img
                        src={logoUrl}
                        alt="logo"
                        style={{
                            width: logoW,
                            height: 'auto',
                            maxHeight: bottomMargin * 0.55,
                            objectFit: 'contain',
                        }}
                    />
                )}
                {overlayText && (
                    <span
                        style={{
                            fontSize,
                            fontWeight: 700,
                            color: textColor,
                            textAlign: 'center',
                            lineHeight: 1.2,
                            wordBreak: 'break-word',
                            fontFamily: 'Georgia, serif',
                        }}
                    >
                        {overlayText}
                    </span>
                )}
                {!overlayText && !logoUrl && (
                    <span style={{
                        fontSize: Math.max(9, fontSize * 0.45),
                        color: '#9ca3af',
                        fontStyle: 'italic',
                    }}>
                        ✎
                    </span>
                )}
            </div>
        </div>
    );
}

// Return true if color is light (luminance > 0.6)
function isLightColor(hex) {
    if (!hex || typeof hex !== 'string') return true;
    const h = hex.replace('#', '');
    if (h.length !== 6) return true;
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    return (0.299 * r + 0.587 * g + 0.114 * b) > 0.6;
}
